'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

export type PhoneInputValue = {
  country: string // ISO2 e.g. 'US'
  dialCode: string // e.g. '+1'
  number: string // local number without dial code
}

export type PhoneInputProps = {
  label?: React.ReactNode
  nameCountry?: string
  nameDialCode?: string
  nameNumber?: string
  defaultCountry?: string
  defaultDialCode?: string
  defaultNumber?: string
  required?: boolean
  className?: string
  onChangeAction?: (value: PhoneInputValue) => void
  disabled?: boolean
}

const COUNTRIES: Array<{ iso2: string; name: string; dial: string }> = [
  { iso2: 'US', name: 'United States', dial: '+1' },
  { iso2: 'IN', name: 'India', dial: '+91' },
  { iso2: 'GB', name: 'United Kingdom', dial: '+44' },
  { iso2: 'CA', name: 'Canada', dial: '+1' },
  { iso2: 'AU', name: 'Australia', dial: '+61' },
]

export default function PhoneInput({
  label,
  nameCountry,
  nameDialCode,
  nameNumber,
  defaultCountry = 'AU',
  defaultDialCode = '+61',
  defaultNumber = '',
  required,
  className,
  onChangeAction,
  disabled = false,
}: PhoneInputProps) {
  const [country, setCountry] = React.useState(defaultCountry)
  const [dial, setDial] = React.useState(defaultDialCode)
  const [number, setNumber] = React.useState(defaultNumber)

  const handleCountryChange = (iso2: string) => {
    setCountry(iso2)
    const found = COUNTRIES.find((c) => c.iso2 === iso2)
    if (found) setDial(found.dial)
    onChangeAction?.({ country: iso2, dialCode: found?.dial ?? dial, number })
  }

  const handleNumberChange = (val: string) => {
    // allow digits, spaces, dashes
    const cleaned = val.replace(/[^0-9\s-]/g, '')
    setNumber(cleaned)
    onChangeAction?.({ country, dialCode: dial, number: cleaned })
  }

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className='text-sm text-foreground [&_*]:text-foreground'>
          {label}
        </label>
      )}
      {/* Single-input appearance */}
      <div
        className={cn(
          'flex items-stretch rounded-md border border-input bg-background overflow-hidden',
          'focus-within:border-[hsl(var(--primary-blue))]',
          disabled && 'opacity-70 cursor-not-allowed'
        )}
      >
        <Select value={country} onValueChange={handleCountryChange}>
          <SelectTrigger
            className={cn(
              'px-3 py-2.5 md:py-2.5 w-28 md:w-28',
              'rounded-none border-0 bg-transparent shadow-none focus:ring-0'
            )}
            disabled={disabled}
            aria-label='Select country'
          >
            {/* Show only dial code in trigger */}
            <span className='text-foreground'>{dial}</span>
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((c) => (
              <SelectItem key={c.iso2} value={c.iso2}>
                {c.name} ({c.dial})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className='my-1 w-px bg-border' aria-hidden />
        <input
          value={number}
          onChange={(e) => handleNumberChange(e.target.value)}
          placeholder='e.g. 555 0100'
          aria-label='Phone number'
          required={required}
          disabled={disabled}
          className={cn(
            'flex-1 px-3 py-2.5 md:py-2.5 outline-none',
            'bg-transparent text-base md:text-sm',
            disabled && 'cursor-not-allowed'
          )}
        />
      </div>

      {/* Hidden inputs to submit via basic forms if names provided */}
      {nameCountry && (
        <input type='hidden' name={nameCountry} value={country} />
      )}
      {nameDialCode && <input type='hidden' name={nameDialCode} value={dial} />}
      {nameNumber && <input type='hidden' name={nameNumber} value={number} />}
    </div>
  )
}
